import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import useWebSocket from '../../hooks/useWebSocket';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { createStreamState, processStreamChunk, finalizeStream } from '../../utils/aiStreamParser';

const MAX_CHARS = 5000;
const SLOW_RESPONSE_THRESHOLD = 10000;

const getCurrentTimeStr = () => {
    const now = new Date();
    return now.toTimeString().split(' ')[0].substring(0, 5);
};

const ToolLogCard = ({ content }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    return (
        <div className="w-full my-1 rounded-xl border border-white/[0.12] bg-[#121218]/90 overflow-hidden shadow-md">
            <div
                className="flex items-center justify-between px-3.5 py-2 bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer select-none transition-colors"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex items-center gap-2 font-mono text-xs text-white">
                    <svg className="w-3.5 h-3.5 stroke-current fill-none stroke-2" viewBox="0 0 24 24">
                        <polyline points="16 18 22 12 16 6" />
                        <polyline points="8 6 2 12 8 18" />
                    </svg>
                    <span className="font-semibold">Tool Execution</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-400">
                        <svg className="w-3 h-3 stroke-current fill-none stroke-[2.5]" viewBox="0 0 24 24">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Done
                    </span>
                    <svg
                        className={`w-3.5 h-3.5 stroke-white/60 fill-none stroke-2 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
                        viewBox="0 0 24 24"
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>
            {!isCollapsed && (
                <div className="p-3 bg-[#040407] border-t border-white/[0.08] font-mono text-xs text-white/75 overflow-x-auto max-h-[200px] leading-relaxed">
                    <pre className="whitespace-pre-wrap break-all">{content}</pre>
                </div>
            )}
        </div>
    );
};

const AIAssistant = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [showInvite, setShowInvite] = useState(false);
    const [inviteText, setInviteText] = useState('');
    const [message, setMessage] = useState('');
    const [chat, setChat] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [showSlowResponseWarning, setShowSlowResponseWarning] = useState(false);
    const { t, language } = useLanguage();
    const isEn = language === 'en';
    const { sendMessage, subscribe, isConnected } = useWebSocket();
    const chatEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const streamStateRef = useRef(createStreamState());
    const inputRef = useRef(null);

    const isActuallyTyping = isTyping || (chat.length > 0 && chat[chat.length - 1]?.isStreaming);

    useEffect(() => {
        return () => {
            if (isActuallyTyping && isConnected) {
                sendMessage('/ai/stop', {});
            }
        };
    }, [isActuallyTyping, isConnected, sendMessage]);

    useEffect(() => {
        const handleOpenEvent = () => setIsOpen(true);
        const handleInviteEvent = (e) => {
            if (!isOpen) {
                setInviteText(e.detail?.message || t('aiAssistantInvite'));
                setShowInvite(true);
            }
        };
        window.addEventListener('openAiAssistant', handleOpenEvent);
        window.addEventListener('showAiInvite', handleInviteEvent);
        return () => {
            window.removeEventListener('openAiAssistant', handleOpenEvent);
            window.removeEventListener('showAiInvite', handleInviteEvent);
        };
    }, [isOpen, t]);

    const handleStreamChunk = useCallback((chunk) => {
        setChat(prev => {
            const result = processStreamChunk(chunk, streamStateRef.current, prev, {});
            streamStateRef.current = result.state;
            return result.chat;
        });
    }, []);

    useEffect(() => {
        if (!isConnected) return;
        const unsubscribe = subscribe('/user/queue/ai/response', (payload) => {
            if (!payload || payload.type !== 'AI_RESPONSE') return;
            if (payload.streaming === false) {
                setIsTyping(false);
                setShowSlowResponseWarning(false);
                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                setChat(prev => finalizeStream(prev, streamStateRef.current, {}));
                streamStateRef.current = createStreamState();
                const finalMsg = payload.data;
                const time = getCurrentTimeStr();
                if (finalMsg === 'ERROR_INSUFFICIENT_PLAN') {
                    setChat(prev => [...prev, { role: 'assistant', content: t('aiAssistantInsufficientPlan'), type: 'text', time }]);
                } else if (finalMsg === 'ERROR_RATE_LIMIT_EXCEEDED') {
                    setChat(prev => [...prev, { role: 'assistant', content: t('ERROR_RATE_LIMIT_EXCEEDED'), type: 'text', time }]);
                } else if (finalMsg === 'ERROR_AI_BANNED') {
                    setChat(prev => [...prev, { role: 'assistant', content: t('ERROR_AI_BANNED'), type: 'text', time }]);
                } else if (finalMsg === 'ERROR_AI_SERVICE_UNAVAILABLE') {
                    setChat(prev => [...prev, { role: 'assistant', content: t('ERROR_AI_SERVICE_UNAVAILABLE'), type: 'text', time }]);
                }
                return;
            }
            if (payload.streaming) {
                setIsTyping(true);
                handleStreamChunk(payload.data || '');
            }
        });
        return () => unsubscribe();
    }, [subscribe, isConnected, t, handleStreamChunk]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chat]);

    // Auto-dismiss invite tooltip after 60 seconds (1 minute)
    useEffect(() => {
        if (!showInvite) return;
        const timer = setTimeout(() => {
            setShowInvite(false);
        }, 60000);
        return () => clearTimeout(timer);
    }, [showInvite]);

    // If chat is opened, dismiss the invite tooltip immediately
    useEffect(() => {
        if (isOpen && showInvite) {
            setShowInvite(false);
        }
    }, [isOpen, showInvite]);

    const handleSend = useCallback(() => {
        if (!message.trim() || !isConnected) return;
        if (message.length > MAX_CHARS) return;

        // If generation is active, stop previous stream and start new
        if (isActuallyTyping) {
            sendMessage('/ai/stop', {});
            streamStateRef.current = createStreamState();
            setChat(prev => {
                const last = prev[prev.length - 1];
                if (last && (last.isStreaming || last.type === 'log')) {
                    return [...prev.slice(0, -1), { ...last, isStreaming: false }];
                }
                return prev;
            });
        }

        const userMsg = { role: 'user', content: message, time: getCurrentTimeStr() };
        setChat(prev => [...prev, userMsg]);
        const moduleNames = {
            '/rcon-hacking': 'RCON Аудит',
            '/account-checker': 'Проверка Аккаунтов',
            '/ddos-testing': 'DDoS Тестирование',
            '/server-data-extraction': 'Извлечение Данных',
            '/bot-connection': 'Боты',
            '/zero-day': 'Zero-day метод',
            '/servers': 'Список серверов',
            '/account': 'Личный кабинет'
        };
        const currentModule = moduleNames[location.pathname] || location.pathname;
        const isReset = chat.length === 0;
        sendMessage('/ai/prompt', {
            prompt: message,
            currentModule: currentModule,
            history: chat.slice(-10),
            resetSession: isReset
        });
        setMessage('');
        setIsTyping(true);
        setShowSlowResponseWarning(false);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            setShowSlowResponseWarning(true);
        }, SLOW_RESPONSE_THRESHOLD);
    }, [message, isConnected, isActuallyTyping, chat, location.pathname, sendMessage]);

    const handleInterrupt = useCallback(() => {
        if (!isConnected) return;
        sendMessage('/ai/stop', {});
        setIsTyping(false);
        streamStateRef.current = createStreamState();
        setChat(prev => {
            const last = prev[prev.length - 1];
            if (last && (last.isStreaming || last.type === 'log')) {
                return [...prev.slice(0, -1), { ...last, isStreaming: false, content: (last.content || '') + ' [Прервано пользователем]' }];
            }
            return prev;
        });
    }, [isConnected, sendMessage]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    const handleToggle = useCallback(() => {
        setIsOpen(prev => !prev);
        setShowInvite(false);
    }, []);

    const handleClearChat = useCallback(() => {
        if (isConnected) {
            const moduleNames = {
                '/rcon-hacking': 'RCON Аудит',
                '/account-checker': 'Проверка Аккаунтов',
                '/ddos-testing': 'DDoS Тестирование',
                '/server-data-extraction': 'Извлечение Данных',
                '/bot-connection': 'Боты',
                '/zero-day': 'Zero-day метод',
                '/servers': 'Список серверов',
                '/account': 'Личный кабинет'
            };
            const currentModule = moduleNames[location.pathname] || location.pathname;
            sendMessage('/ai/reset', { currentModule });
        }
        setIsTyping(false);
        setChat([]);
        streamStateRef.current = createStreamState();
    }, [isConnected, location.pathname, sendMessage]);

    return (
        <div className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-[9999] flex flex-col items-end pointer-events-none">

            {/* ═══ INVITE BUBBLE ═══ */}
            <AnimatePresence>
                {showInvite && !isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="mb-2 sm:mb-3 pointer-events-auto max-w-[260px] sm:max-w-[290px]"
                    >
                        <div className="relative bg-[#0e0e12]/95 backdrop-blur-xl border border-white/[0.14] rounded-2xl rounded-br-sm px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-2xl shadow-black/80">
                            <button
                                onClick={() => setShowInvite(false)}
                                className="absolute top-2 right-2 sm:top-2.5 sm:right-2.5 w-4 h-4 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.1] transition-colors"
                            >
                                <svg className="w-2.5 h-2.5 stroke-current fill-none stroke-[2]" viewBox="0 0 24 24">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                            <div className="flex items-center gap-1.5 mb-1 sm:mb-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                <span className="text-xs font-semibold text-white">StormAI</span>
                            </div>
                            <p className="text-xs sm:text-[13px] text-white/90 font-medium leading-snug pr-2">{inviteText}</p>
                            <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-[#0e0e12] border-r border-b border-white/[0.14] rotate-45"></div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ═══ CHAT WINDOW (ZeroDay Method Aesthetic) ═══ */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="mb-3 sm:mb-4 w-[calc(100vw-32px)] sm:w-[380px] md:w-[420px] max-w-[420px] bg-[#0e0e12]/95 backdrop-blur-2xl border border-white/[0.1] rounded-[22px] sm:rounded-[28px] shadow-[0_25px_70px_rgba(0,0,0,0.85)] overflow-hidden flex flex-col h-[calc(100dvh-125px)] sm:h-[590px] max-h-[640px] pointer-events-auto"
                    >
                        {/* ─── Header ─── */}
                        <div className="px-4 py-3 sm:px-5 sm:py-3.5 border-b border-white/[0.08] bg-white/[0.02] flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-2 sm:gap-2.5">
                                <div className="relative flex items-center justify-center">
                                    <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-emerald-400"></span>
                                    <span className="absolute w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-emerald-400 animate-ping opacity-75"></span>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                        <span className="font-bold text-xs sm:text-sm text-white tracking-tight">StormAI</span>
                                        <span className="px-1.5 py-0.5 rounded-full bg-white/[0.08] text-[9px] sm:text-[10px] font-mono text-white/60">v2.4</span>
                                    </div>
                                    <p className="text-[10px] sm:text-[11px] text-white/40 font-mono leading-none mt-0.5">
                                        {t('aiAssistantTitle') || 'Autonomous AI Assistant'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-1.5">
                                {chat.length > 0 && (
                                    <button
                                        onClick={handleClearChat}
                                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/[0.06] border border-white/[0.1] text-white/60 hover:text-white hover:bg-white/[0.12] transition-colors flex items-center justify-center cursor-pointer"
                                        title={isEn ? "Clear chat" : "Очистить чат"}
                                    >
                                        <svg className="w-3.5 h-3.5 stroke-current fill-none stroke-[2]" viewBox="0 0 24 24">
                                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                        </svg>
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/[0.06] border border-white/[0.1] text-white/60 hover:text-white hover:bg-white/[0.12] transition-colors flex items-center justify-center cursor-pointer"
                                    title={isEn ? "Close" : "Закрыть"}
                                >
                                    <svg className="w-3.5 h-3.5 stroke-current fill-none stroke-[2.2]" viewBox="0 0 24 24">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* ─── Chat Area (Apple/Grok clean bubbles, no avatars) ─── */}
                        <div className={`flex-1 ${chat.length === 0 ? 'overflow-hidden flex flex-col justify-center' : 'overflow-y-auto overscroll-contain'} p-3 sm:p-4 space-y-3 sm:space-y-3.5 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.15)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-white/30`}>

                            {/* Not logged in */}
                            {!user && (
                                <div className="text-center px-4 py-6 sm:px-6 sm:py-8 mx-2">
                                    <div className="flex items-center justify-center mx-auto mb-3">
                                        <i className="fas fa-lock text-xl sm:text-2xl text-white/50"></i>
                                    </div>
                                    <p className="text-white/60 text-xs leading-relaxed">{t('aiAssistantLoginRequired')}</p>
                                </div>
                            )}

                            {/* Empty state (Clean symbol with NO border around it) */}
                            {user && chat.length === 0 && (
                                <div className="text-center px-4">
                                    <div className="flex items-center justify-center mx-auto mb-3">
                                        <i className="fas fa-user-secret text-2xl sm:text-3xl text-white/75"></i>
                                    </div>
                                    <h4 className="text-white font-medium text-sm mb-1.5">StormAI Assistant</h4>
                                    <p className="text-white/40 text-xs leading-relaxed max-w-[260px] mx-auto">
                                        {t('aiAssistantWelcome')}
                                    </p>
                                </div>
                            )}

                            {/* Messages */}
                            {chat.map((msg, i) => (
                                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} w-full`}>

                                    {msg.type === 'status' ? (
                                        /* ═══ STATUS PILL ═══ */
                                        <div className="w-full flex items-center gap-2.5 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white/75 font-mono text-xs">
                                            <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping shrink-0"></span>
                                            <span className="leading-tight">{msg.content}</span>
                                        </div>

                                    ) : msg.type === 'log' ? (
                                        /* ═══ TOOL LOG CARD (ZeroDay Style) ═══ */
                                        <ToolLogCard content={msg.content} />

                                    ) : msg.role === 'user' ? (
                                        /* ═══ USER MESSAGE BUBBLE (High-contrast pure white) ═══ */
                                        <div className="max-w-[88%] sm:max-w-[85%] bg-white text-black font-medium rounded-[18px] sm:rounded-[20px] rounded-tr-[4px] px-3.5 py-2 sm:px-4 sm:py-2.5 text-[0.84rem] sm:text-[0.88rem] leading-relaxed shadow-sm">
                                            <div className="prose prose-sm max-w-none text-black [&_p]:mb-1 [&_p:last-child]:mb-0 [&_code]:bg-black/10 [&_code]:text-black [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-xs">
                                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                                            </div>
                                            {msg.time && (
                                                <span className="block text-[0.68rem] text-black/45 text-right mt-1 font-mono tracking-tight">
                                                    {msg.time}
                                                </span>
                                            )}
                                        </div>

                                    ) : (
                                        /* ═══ ASSISTANT MESSAGE BUBBLE (Subtle frosted dark) ═══ */
                                        <div className="max-w-[92%] sm:max-w-[88%] bg-white/[0.05] border border-white/[0.12] text-[#f0f0f4] rounded-[18px] sm:rounded-[20px] rounded-tl-[4px] px-3.5 py-2 sm:px-4 sm:py-2.5 text-[0.84rem] sm:text-[0.88rem] leading-relaxed">
                                            <div className="prose prose-invert prose-sm max-w-none text-[#f0f0f4] [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_code]:bg-white/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-xs [&_a]:text-cyan-400 [&_a]:underline">
                                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                                            </div>
                                            {msg.time && (
                                                <span className="block text-[0.68rem] text-white/40 mt-1.5 font-mono tracking-tight">
                                                    {msg.time}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Thinking State Animation (AI Studio Style) */}
                            {isTyping && (
                                <div className="flex justify-start w-full animate-[fadeInThinking_0.2s_ease_forwards]">
                                    <div className="bg-white/[0.05] border border-white/[0.12] rounded-[18px] sm:rounded-[20px] rounded-tl-[4px] px-3.5 py-1.5 sm:px-4 sm:py-2 flex items-center">
                                        <span className="thinking-text-shimmer text-xs sm:text-sm">Thinking</span>
                                    </div>
                                </div>
                            )}

                            {/* Slow response indicator */}
                            {showSlowResponseWarning && isTyping && (
                                <div className="text-center py-1">
                                    <span className="text-[10px] sm:text-[11px] text-white/40 font-mono inline-flex items-center gap-1.5">
                                        <i className="fas fa-hourglass-half text-amber-400/70"></i>
                                        {isEn ? 'Processing complex analysis...' : 'Обработка комплексного запроса...'}
                                    </span>
                                </div>
                            )}

                            {chat.length > 0 && <div ref={chatEndRef} />}
                        </div>

                        {/* ─── Input Bar (ZeroDay Capsule Style) ─── */}
                        <div className="p-2.5 sm:p-3.5 border-t border-white/[0.08] bg-[#0e0e12]/98 shrink-0">
                            <div className="relative flex items-center bg-white/[0.04] border border-transparent focus-within:border-white rounded-full px-2.5 sm:px-3 py-1 sm:py-1.5 gap-2 transition-all duration-200">
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/40 shrink-0 ml-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="7" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>

                                <textarea
                                    ref={inputRef}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t('aiAssistantPlaceholder') || (isEn ? 'Describe task or issue...' : 'Опиши задачу или проблему...')}
                                    disabled={!user}
                                    rows={1}
                                    style={{ minHeight: '26px', maxHeight: '100px' }}
                                    className="flex-1 bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-[16px] sm:text-[0.88rem] text-white placeholder-white/40 py-1 resize-none leading-relaxed disabled:opacity-40 disabled:cursor-not-allowed"
                                    onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                />

                                <div className="flex items-center gap-1.5 shrink-0">
                                    {isActuallyTyping && (
                                        <button
                                            onClick={handleInterrupt}
                                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer active:scale-95 bg-red-500 hover:bg-red-600 text-white shadow-[0_0_12px_rgba(239,68,68,0.5)]"
                                            title={isEn ? 'Stop generation' : 'Остановить генерацию'}
                                        >
                                            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-current" viewBox="0 0 24 24">
                                                <rect x="6" y="6" width="12" height="12" rx="2" />
                                            </svg>
                                        </button>
                                    )}

                                    {(!isActuallyTyping || message.trim()) && (
                                        <button
                                            onClick={handleSend}
                                            disabled={!user || !message.trim() || message.length > MAX_CHARS}
                                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer active:scale-95 bg-white hover:scale-105 text-black disabled:opacity-30 disabled:scale-100 disabled:cursor-not-allowed shadow-[0_2px_10px_rgba(255,255,255,0.3)]"
                                            title={isEn ? 'Send message' : 'Отправить'}
                                        >
                                            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 stroke-current fill-none stroke-[2.2] translate-x-[0.5px]" viewBox="0 0 24 24">
                                                <line x1="22" y1="2" x2="11" y2="13" />
                                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between items-center px-2 pt-1 sm:pt-1.5 text-[9px] sm:text-[10px] font-mono text-white/35">
                                <span>{message.length > MAX_CHARS ? t('aiAssistantCharLimit') : (isEn ? 'Enter to send' : 'Enter для отправки')}</span>
                                <span>{message.length}/{MAX_CHARS}</span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ═══ FLOATING FAB BUTTON ═══ */}
            <button
                onClick={handleToggle}
                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-[18px] sm:rounded-[22px] flex items-center justify-center shadow-[0_8px_28px_rgba(43,127,255,0.4)] hover:shadow-[0_12px_36px_rgba(43,127,255,0.55)] transition-all ease-snappy duration-200 pointer-events-auto active:scale-95 relative ${
                    isOpen
                        ? 'bg-[#121218] text-white border border-white/20 shadow-none'
                        : 'bg-gradient-to-b from-[#2b7fff] to-[#00b4d8] text-white'
                }`}
                title={isOpen ? (isEn ? "Close AI Assistant" : "Закрыть ИИ Ассистент") : (isEn ? "Open AI Assistant" : "Открыть ИИ Ассистент")}
            >
                {isOpen ? (
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 stroke-current fill-none stroke-[2.2]" viewBox="0 0 24 24">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                ) : (
                    <i className="fas fa-user-secret text-xl sm:text-2xl text-white"></i>
                )}

                {/* Notification badge dot */}
                {!isOpen && showInvite && (
                    <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#ef4444] rounded-full border-2 border-[#08090d] shadow-sm" />
                )}
            </button>
        </div>
    );
};

export default AIAssistant;